import React from 'react'
import { useTranslation } from 'react-i18next'

import { useProductReadContext } from '@context/ProductContext'

import { ListItem } from '@ui/components/List/ListItem/ListItem'
import { ArrowIcon } from '@ui/icons/Arrow.icon'

import { WalletPaymentProvider } from '@model/WalletPaymentProvider'

import { useFormatNumber } from '@hooks/useFormatNumber'

import PSPLogo from '@assets/images/online-banking-icon-48x48px.png'

const SC_TRANSACT365_MYR_ID = 'b0f704f5-628a-11ee-96cf-06bf40e8b50a'
const SC_TRANSACT365_BDT = '9e064c72-7e03-11ee-96cf-06bf40e8b50a'
const SINGAPORE_ONLINE_BANK_TRANSFER_METHOD_ID = 'b82dc307-9d79-11ed-8b0c-0a51dcb142a4'
const PRO_TRADERS_GROUP_METHOD_ID = 'd09f91d5-2c6d-4d25-9f91-d52c6d1d2555'

const WARNING_MESSAGE_KEYS: Record<string, string> = {
  [SINGAPORE_ONLINE_BANK_TRANSFER_METHOD_ID]: 'The service is unavailable from 4 PM to 9 AM',
  [SC_TRANSACT365_MYR_ID]: 'Service hours',
  [SC_TRANSACT365_BDT]: 'Service hours SC_TRANSACT365_BDT',
}

// TODO remove when BE provides configurable supported methods for Payment Agents
const SUPPORTED_METHODS_BY_METHOD_ID: Record<string, string> = {
  [PRO_TRADERS_GROUP_METHOD_ID]: 'Payment Agent.Pro Traders Group supported methods',
}

interface PaymentProviderListItemProps {
  provider: WalletPaymentProvider
  disabled?: boolean
  variant?: 'deposit' | 'withdrawal'
  onClick: () => void
}

export const PaymentProviderListItem: React.FC<PaymentProviderListItemProps> = ({
  provider,
  disabled,
  variant = 'deposit',
  onClick,
}) => {
  const { t } = useTranslation()
  const { formatMoney } = useFormatNumber()
  const { isDefaultCFDProductType } = useProductReadContext()
  const isProductTypeCFD = isDefaultCFDProductType()

  const paymentDetailsText = isProductTypeCFD
    ? [
        provider.fundingTime,
        provider.fee === 0
          ? t('No fee')
          : `${t('Fee')} ${formatMoney(provider.fee, provider.currency.id)}`,
        t('Min {{amount}}', {
          amount: formatMoney(provider.minAmount, provider.currency.id),
        }),
      ].join(' \u2022 ')
    : ''

  const showLastUsedBadge =
    variant === 'deposit' && isProductTypeCFD && provider.lastDeposit === true
  const warningMessageKey = WARNING_MESSAGE_KEYS[provider.method.id]
  const supportedMethodsText = SUPPORTED_METHODS_BY_METHOD_ID[provider.method.id]
  const maintenanceMessage = provider.maintenanceMessage?.trim()

  const paymentDetailsSubtitle = [
    paymentDetailsText,
    maintenanceMessage || undefined,
    warningMessageKey ? t(warningMessageKey) : undefined,
  ]
    .filter(Boolean)
    .join('\n')

  const subtitle = supportedMethodsText
    ? [
        {
          text: t(supportedMethodsText),
          color: 'text-low-em' as const,
          italic: true,
        },
        ...(paymentDetailsSubtitle ? [paymentDetailsSubtitle] : []),
      ]
    : paymentDetailsSubtitle

  return (
    <ListItem
      mobileHoverDisabled
      disabled={disabled}
      onClick={onClick}
      startSlot={{
        shape: 'circle',
        bg: 'surface-white',
        border: 'outline-low-em',
        bgImage: provider.logo || PSPLogo,
        bgImageFit: 'contain',
        size: 'x56',
      }}
      endSlot={{ icon: { icon: ArrowIcon.Right } }}
      subtitle={subtitle}
      subtitleSlot={showLastUsedBadge ? { badgeText: t('Last used') } : undefined}
    >
      {provider.description}
    </ListItem>
  )
}
